import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useCustomerWorkspace } from "../context/CustomerWorkspaceContext";
import {
  AutoRefillSettings,
  TOPUP_PRESETS,
  createTopupSession,
  updateAutoRefill,
} from "../services/billingService";
import { fetchUsageSummary, UsageSummary } from "../services/usageService";
import TeamSeatsCard from "../features/user-management/components/TeamSeatsCard";

const BillingPage: React.FC = () => {
  const { idToken: rawIdToken } = useAuth();
  const idToken = rawIdToken || "";
  const { selectedCustomerId, selectedCustomerName } = useCustomerWorkspace();

  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [topupLoading, setTopupLoading] = useState<number | null>(null);
  const [autoRefillLoading, setAutoRefillLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!selectedCustomerId || !idToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUsageSummary(selectedCustomerId, idToken);
      setUsage(data);
    } catch {
      setError("Failed to load billing info. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerId, idToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTopup = async (amount: number) => {
    if (!selectedCustomerId || !idToken) return;
    setTopupLoading(amount);
    setError(null);
    try {
      const { url } = await createTopupSession(selectedCustomerId, amount, idToken);
      window.location.href = url;
    } catch {
      setError("Failed to create checkout session. Please try again.");
      setTopupLoading(null);
    }
  };

  const handleAutoRefillToggle = async () => {
    if (!usage || !selectedCustomerId || !idToken) return;
    const newSettings: AutoRefillSettings = {
      auto_refill_enabled: !usage.auto_refill_enabled,
      auto_refill_threshold: usage.auto_refill_threshold,
      auto_refill_amount: usage.auto_refill_amount,
    };
    setAutoRefillLoading(true);
    setError(null);
    try {
      await updateAutoRefill(selectedCustomerId, newSettings, idToken);
      setUsage((prev) => prev ? { ...prev, ...newSettings } : prev);
    } catch {
      setError("Failed to update auto-refill settings.");
    } finally {
      setAutoRefillLoading(false);
    }
  };

  const handleAutoRefillAmountChange = async (amount: number) => {
    if (!usage || !selectedCustomerId || !idToken) return;
    const newSettings: AutoRefillSettings = {
      auto_refill_enabled: usage.auto_refill_enabled,
      auto_refill_threshold: usage.auto_refill_threshold,
      auto_refill_amount: amount,
    };
    try {
      await updateAutoRefill(selectedCustomerId, newSettings, idToken);
      setUsage((prev) => prev ? { ...prev, ...newSettings } : prev);
    } catch {
      setError("Failed to update auto-refill amount.");
    }
  };

  const balance = usage?.credit_balance ?? 0;
  const isLowBalance = balance < 2;

  return (
    <div className="p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-theme-fg">Credits & Billing</h1>
          {selectedCustomerName && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 shadow-sm ring-1 ring-inset ring-green-600/20 dark:bg-green-900/20 dark:text-green-400 dark:ring-green-500/20">
              <span className="material-icons-outlined text-[18px] text-amber-600 opacity-90 dark:text-amber-500">storefront</span>
              <span>{selectedCustomerName}</span>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Balance card */}
        <div className={`rounded-xl border p-6 shadow-sm ${isLowBalance ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20" : "border-theme-border bg-theme-surface"}`}>
          <p className="text-sm font-medium text-theme-muted">Current Balance</p>
          {loading ? (
            <div className="mt-2 h-10 w-32 animate-pulse rounded-md bg-theme-border" />
          ) : (
            <p className={`mt-1 text-4xl font-bold ${isLowBalance ? "text-amber-600 dark:text-amber-400" : "text-theme-fg"}`}>
              ${balance.toFixed(2)}
            </p>
          )}
          {isLowBalance && !loading && (
            <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
              Low balance — add credits to keep generating content.
            </p>
          )}
        </div>

        {/* Top-up presets */}
        <div className="rounded-xl border border-theme-border bg-theme-surface p-6 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-theme-fg">Add Credits</h2>
          <div className="grid grid-cols-3 gap-3">
            {TOPUP_PRESETS.map((amount) => (
              <button
                key={amount}
                onClick={() => handleTopup(amount)}
                disabled={topupLoading !== null || !selectedCustomerId}
                className="flex flex-col items-center justify-center rounded-lg border border-theme-border bg-theme-bg py-4 text-sm font-medium text-theme-fg transition-all hover:border-theme-primary hover:bg-theme-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {topupLoading === amount ? (
                  <span className="animate-pulse">Loading…</span>
                ) : (
                  <>
                    <span className="text-xl font-bold text-theme-primary">${amount}</span>
                    <span className="mt-1 text-xs text-theme-muted">Add ${amount}</span>
                  </>
                )}
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-theme-muted">Minimum top-up: $15. Payments are secure via Stripe.</p>
        </div>

        {/* Auto-refill */}
        <div className="rounded-xl border border-theme-border bg-theme-surface p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-theme-fg">Auto-Refill</h2>
              <p className="mt-1 text-sm text-theme-muted">
                Automatically top up when your balance drops below ${usage?.auto_refill_threshold?.toFixed(2) ?? "2.00"}.
              </p>
            </div>
            <button
              onClick={handleAutoRefillToggle}
              disabled={autoRefillLoading || loading || !selectedCustomerId}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 ${usage?.auto_refill_enabled ? "bg-theme-primary" : "bg-gray-300 dark:bg-gray-600"}`}
              role="switch"
              aria-checked={usage?.auto_refill_enabled}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${usage?.auto_refill_enabled ? "translate-x-5" : "translate-x-0"}`}
              />
            </button>
          </div>

          {usage?.auto_refill_enabled && (
            <div className="mt-4">
              <p className="mb-2 text-sm font-medium text-theme-fg">Refill amount</p>
              <div className="flex flex-wrap gap-2">
                {TOPUP_PRESETS.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleAutoRefillAmountChange(amount)}
                    className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${usage.auto_refill_amount === amount ? "bg-theme-primary text-white" : "border border-theme-border bg-theme-bg text-theme-fg hover:border-theme-primary"}`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Team (SGL-398): who holds Seats, the plan's caps, and inviting a
            teammate or an agency operator. */}
        {usage && selectedCustomerId && (
          <TeamSeatsCard
            customerId={selectedCustomerId}
            businessName={selectedCustomerName || ""}
            usage={usage}
            idToken={idToken}
            onChanged={fetchData}
          />
        )}

        {/* Links */}
        <div className="rounded-xl border border-theme-border bg-theme-surface p-6 shadow-sm">
          <h2 className="mb-3 text-base font-semibold text-theme-fg">Manage Subscription</h2>
          <a
            href="https://app.sagelyn.ai/settings"
            className="inline-flex items-center gap-2 text-sm text-theme-primary hover:underline"
          >
            <span className="material-icons-outlined text-[16px]">open_in_new</span>
            Subscription &amp; payment methods
          </a>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
